'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, BookOpen, Bot, Users, Shield, Trophy, Settings, HelpCircle, GraduationCap, TreePine, X, ChevronRight, Upload, Building2, Library, GitBranch, Zap,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  role: 'student' | 'parent' | 'professional' | 'admin';
  onClose: () => void;
}

const navByRole = {
  student: [
    {
      group: 'Learning',
      items: [
        { label: 'Dashboard', href: '/student-dashboard', icon: LayoutDashboard },
        { label: 'Course Catalog', href: '/course-catalog', icon: BookOpen, badge: 'New' },
        { label: 'My AI Tutor', href: '/ai-tutor-builder', icon: Bot },
        { label: 'Special Libraries', href: '/curriculum-libraries', icon: Library },
        { label: 'Achievements', href: '/student-dashboard', icon: Trophy },
      ],
    },
    {
      group: 'Campus',
      items: [
        { label: 'Campus Home', href: '/', icon: TreePine },
        { label: 'Help Desk', href: '/', icon: HelpCircle },
      ],
    },
  ],
  parent: [
    {
      group: 'Overview',
      items: [
        { label: 'Parent Dashboard', href: '/parent-dashboard', icon: LayoutDashboard },
        { label: 'My Children', href: '/parent-dashboard', icon: Users, badge: '2' },
        { label: 'Permissions', href: '/parent-dashboard', icon: Shield, badge: '3' },
        { label: 'Progress Reports', href: '/parent-dashboard', icon: GraduationCap },
      ],
    },
    {
      group: 'Campus',
      items: [
        { label: 'Campus Home', href: '/', icon: TreePine },
        { label: 'Settings', href: '/parent-dashboard', icon: Settings },
        { label: 'Help Desk', href: '/', icon: HelpCircle },
      ],
    },
  ],
  professional: [
    {
      group: 'Learning',
      items: [
        { label: 'Dashboard', href: '/student-dashboard', icon: LayoutDashboard },
        { label: 'Course Catalog', href: '/course-catalog', icon: BookOpen },
        { label: 'Special Libraries', href: '/curriculum-libraries', icon: Library },
        { label: 'Certifications', href: '/course-catalog', icon: Trophy },
        { label: 'AI Tutor', href: '/ai-tutor-builder', icon: Bot },
      ],
    },
    {
      group: 'Educator Tools',
      items: [
        { label: 'Professor Portal', href: '/professor-portal', icon: Upload, badge: 'New' },
        { label: 'Institutional', href: '/institutional-integration', icon: Building2 },
      ],
    },
    {
      group: 'Campus',
      items: [
        { label: 'Campus Home', href: '/', icon: TreePine },
        { label: 'Help Desk', href: '/', icon: HelpCircle },
      ],
    },
  ],
  admin: [
    {
      group: 'Control',
      items: [
        { label: 'Dashboard', href: '/student-dashboard', icon: LayoutDashboard },
        { label: 'Users', href: '/parent-dashboard', icon: Users },
        { label: 'Courses', href: '/course-catalog', icon: BookOpen },
        { label: 'Permissions', href: '/parent-dashboard', icon: Shield },
        { label: 'AI Tutors', href: '/ai-tutor-builder', icon: Bot },
      ],
    },
    {
      group: 'Curriculum',
      items: [
        { label: 'Curriculum Mgmt', href: '/curriculum-management', icon: GitBranch, badge: 'New' },
        { label: 'Professor Portal', href: '/professor-portal', icon: Upload },
        { label: 'Institutional', href: '/institutional-integration', icon: Building2 },
        { label: 'Special Libraries', href: '/curriculum-libraries', icon: Library },
        { label: 'AI Tutor Sync', href: '/ai-tutor-sync', icon: Zap },
      ],
    },
    {
      group: 'Campus',
      items: [
        { label: 'Campus Home', href: '/', icon: TreePine },
        { label: 'Settings', href: '/parent-dashboard', icon: Settings },
      ],
    },
  ],
};

export default function Sidebar({ collapsed, mobileOpen, role, onClose }: SidebarProps) {
  const pathname = usePathname();
  const navGroups = navByRole[role];

  const sidebarClasses = `
    fixed top-0 left-0 h-full z-40 bg-card border-r border-border flex flex-col sidebar-transition
    ${collapsed ? 'w-16' : 'w-60'}
    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
    lg:translate-x-0 lg:static lg:z-auto
  `;

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-base text-foreground tracking-tight">JarvondisU</span>
        )}
        <button
          onClick={onClose}
          className="ml-auto lg:hidden p-1 rounded-md hover:bg-muted transition-colors"
          aria-label="Close sidebar"
        >
          <X size={16} className="text-muted-foreground" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navGroups.map((group) => (
          <div key={`group-${group.group}`} className="mb-4">
            {!collapsed && (
              <p className="text-xs font-600 tracking-widest uppercase text-muted-foreground px-3 mb-2">
                {group.group}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.href;
              return (
                <Link
                  key={`nav-${item.label}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all duration-150 group
                    ${isActive
                      ? 'bg-primary/10 text-primary font-semibold' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="text-sm flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="text-xs bg-accent text-accent-foreground px-1.5 py-0.5 rounded-full font-semibold">
                          {item.badge}
                        </span>
                      )}
                      {isActive && <ChevronRight size={14} className="text-primary" />}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className={`p-4 border-t border-border ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary">
                {role === 'student' ? 'S' : role === 'parent' ? 'P' : role === 'professional' ? 'PR' : 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {role === 'student' ? 'Amara Osei' : role === 'parent' ? 'David Osei' : 'Dr. Nadia Volkov'}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{role}</p>
            </div>
            <Settings size={14} className="text-muted-foreground shrink-0" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">
              {role === 'student' ? 'S' : role === 'parent' ? 'P' : 'A'}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}